import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { SocketContext } from '../../socket';
import { missedCallsApi, errorMessage } from './missedCallsApi';
import { filtersToParams, rowMatchesFilters, upsertSorted, withinLoaded } from './missedCallFormat';

const PAGE = 50;
const MAX_PAGE = 100;
const POLL_MS = 60000;

/**
 * The page's rows (docs/MISSED_CALLS_PLAN.md §6.3): keyset pages, newest day first; live `missedCalls.upsert`
 * merges; after a reconnect, what is loaded is fetched again. A row that stops matching the filters (e.g. ticked
 * 완료 under 미처리만) stays, dimmed (`stale`), until the filters change — nothing vanishes under someone's
 * cursor. A delete leaves the normal view at once. The page connects the shared socket; this never does.
 */
export function useMissedCallsFeed({ filters, meId }) {
    const socket = useContext(SocketContext);
    const [rows, setRows] = useState([]);
    const [nextCursor, setNextCursor] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [stale, setStale] = useState(() => new Set());
    const generation = useRef(0); // a response from an older filter set is dropped
    const latest = useRef({ rows, nextCursor, filters, meId, loading });
    latest.current = { rows, nextCursor, filters, meId, loading };

    const fetchPage = useCallback((cursor, limit = PAGE) => missedCallsApi.list({
        ...filtersToParams(latest.current.filters),
        limit,
        ...(cursor ? { cursor } : {}),
    }), []);

    const reload = useCallback(async () => {
        const mine = ++generation.current;
        setLoading(true);
        try {
            const page = await fetchPage(null);
            if (mine !== generation.current) return;
            setRows(page.rows);
            setNextCursor(page.next_cursor);
            setStale(new Set());
            setError(null);
        } catch (err) {
            if (mine === generation.current) setError(errorMessage(err));
        } finally {
            if (mine === generation.current) setLoading(false);
        }
    }, [fetchPage]);

    // New filters → the first page again.
    const filterKey = JSON.stringify(filtersToParams(filters));
    useEffect(() => { reload(); }, [filterKey, reload]);

    const loadMore = useCallback(async () => {
        const { nextCursor: cursor, loading: busy } = latest.current;
        if (!cursor || busy) return;
        const mine = generation.current;
        setLoading(true);
        try {
            const page = await fetchPage(cursor);
            if (mine !== generation.current) return;
            setRows((prev) => page.rows.reduce(upsertSorted, prev));
            setNextCursor(page.next_cursor);
        } catch (err) {
            if (mine === generation.current) setError(errorMessage(err));
        } finally {
            if (mine === generation.current) setLoading(false);
        }
    }, [fetchPage]);

    /** Fetch again as many rows as are loaded (page by page) — after a reconnect, or while the socket is down. */
    const reloadLoaded = useCallback(async () => {
        const mine = ++generation.current;
        const want = Math.max(latest.current.rows.length, PAGE);
        try {
            let all = [];
            let cursor = null;
            do {
                const page = await fetchPage(cursor, Math.min(MAX_PAGE, want - all.length));
                if (mine !== generation.current) return;
                all = all.concat(page.rows);
                cursor = page.next_cursor;
            } while (cursor && all.length < want);
            setRows(all);
            setNextCursor(cursor);
            setStale(new Set());
            setError(null);
        } catch (err) {
            if (mine === generation.current) setError(errorMessage(err));
        }
    }, [fetchPage]);

    /** Merge one row (a socket event or an API response). */
    const upsert = useCallback((row) => {
        if (!row || row.id == null) return;
        const { filters: f, meId: me, nextCursor: cursor } = latest.current;
        const matches = rowMatchesFilters(row, f, me);
        const leaves = Boolean(row.deleted_at) && !f.deleted;
        setRows((prev) => {
            if (prev.some((r) => r.id === row.id)) {
                return leaves ? prev.filter((r) => r.id !== row.id) : upsertSorted(prev, row);
            }
            return matches && withinLoaded(row, prev, Boolean(cursor)) ? upsertSorted(prev, row) : prev;
        });
        setStale((prev) => {
            const shouldBe = !matches && !leaves;
            if (shouldBe === prev.has(row.id)) return prev;
            const next = new Set(prev);
            if (shouldBe) next.add(row.id); else next.delete(row.id);
            return next;
        });
    }, []);

    useEffect(() => {
        if (!socket) return undefined;
        const onUpsert = (row) => upsert(row);
        const onReconnect = () => reloadLoaded();
        socket.on('missedCalls.upsert', onUpsert);
        const manager = socket.io;
        if (manager && typeof manager.on === 'function') manager.on('reconnect', onReconnect);
        // While the socket is down, a light poll keeps the page roughly current.
        const poll = setInterval(() => {
            if (socket.connected || (typeof document !== 'undefined' && document.visibilityState !== 'visible')) return;
            reloadLoaded();
        }, POLL_MS);
        return () => {
            socket.off('missedCalls.upsert', onUpsert);
            if (manager && typeof manager.off === 'function') manager.off('reconnect', onReconnect);
            clearInterval(poll);
        };
    }, [socket, upsert, reloadLoaded]);

    return { rows, loading, error, hasMore: Boolean(nextCursor), loadMore, reload, upsert, stale };
}

export default useMissedCallsFeed;
